import { useEffect, useMemo, useState } from "react";
import { api, formatApiErrorDetail } from "@/api/client";
import { REMINDER_PRIORITY, fmtDateTime } from "@/lib/domain";
import { Chip } from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Bell, Check, Clock } from "lucide-react";
import { toast, Toaster } from "sonner";

const empty = { title: "", description: "", type: "manual", priority: "normal", due_at: "" };

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

export default function Reminders() {
  const [reminders, setReminders] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [tab, setTab] = useState("today");

  const load = () => api.get("/reminders").then((r) => setReminders(r.data));
  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(empty); setOpen(true); };

  const save = async () => {
    if (!form.title.trim()) {
      toast.error("Укажите название");
      return;
    }
    try {
      await api.post("/reminders", { ...form, due_at: form.due_at ? new Date(form.due_at).toISOString() : null });
      toast.success("Напоминание создано");
      setOpen(false);
      load();
    } catch (e) {
      toast.error("Ошибка: " + formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const complete = async (id) => {
    await api.post(`/reminders/${id}/complete`);
    load();
  };

  const snooze = async (id) => {
    const input = window.prompt("Отложить до (ГГГГ-ММ-ДД ЧЧ:ММ)");
    if (!input) return;
    const date = new Date(input);
    if (isNaN(date.getTime())) {
      toast.error("Неверный формат даты");
      return;
    }
    await api.post(`/reminders/${id}/snooze`, { until: date.toISOString() });
    load();
  };

  const groups = useMemo(() => {
    const now = new Date();
    const today = [], overdue = [], upcoming = [], done = [];
    for (const r of reminders) {
      if (r.status === "done" || r.status === "cancelled") {
        done.push(r);
        continue;
      }
      if (!r.due_at) {
        upcoming.push(r);
        continue;
      }
      const due = new Date(r.due_at);
      if (due < now && !isSameDay(due, now)) overdue.push(r);
      else if (isSameDay(due, now)) today.push(r);
      else upcoming.push(r);
    }
    return { today, overdue, upcoming, done };
  }, [reminders]);

  const renderList = (list, showActions = true) => (
    <div className="space-y-2">
      {list.length === 0 && (
        <Card className="p-8 bg-[#131924] border-[#212b3d] text-center text-slate-500">
          <Bell className="mx-auto mb-2 opacity-50" size={24} />
          Пусто.
        </Card>
      )}
      {list.map((r) => {
        const prio = REMINDER_PRIORITY[r.priority] || REMINDER_PRIORITY.normal;
        return (
          <Card key={r.id} data-testid={`reminder-card-${r.id}`} className="p-4 bg-[#131924] border-[#212b3d] flex items-center justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <div className="font-medium text-slate-100 truncate">{r.title}</div>
                <Chip className={prio.cls}>{prio.label}</Chip>
              </div>
              {r.description && <div className="text-xs text-slate-500 mb-1 truncate">{r.description}</div>}
              <div className="text-[11px] text-slate-600 flex items-center gap-1">
                <Clock size={11} /> {r.due_at ? fmtDateTime(r.due_at) : "без срока"}
              </div>
            </div>
            {showActions && (
              <div className="flex gap-2 shrink-0">
                <Button size="sm" variant="outline" data-testid={`snooze-${r.id}`} onClick={() => snooze(r.id)}
                  className="bg-transparent border-[#212b3d] hover:bg-[#1c2434]">Отложить</Button>
                <Button size="sm" data-testid={`complete-${r.id}`} onClick={() => complete(r.id)}
                  className="bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 hover:bg-emerald-500/30">
                  <Check size={14} />
                </Button>
              </div>
            )}
          </Card>
        );
      })}
    </div>
  );

  return (
    <div className="space-y-6">
      <Toaster theme="dark" position="top-right" />
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="text-[11px] uppercase tracking-[0.3em] text-amber-400/80 mb-2 font-medium">Grandmont Group</div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl">Напоминания</h1>
          <p className="text-slate-400 mt-1 text-sm">{reminders.filter((r) => r.status !== "done" && r.status !== "cancelled").length} активных</p>
        </div>
        <Button data-testid="new-reminder-button" onClick={startNew} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">
          <Plus size={16} /> Новое напоминание
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-[#131924] border border-[#212b3d]">
          <TabsTrigger value="today" data-testid="tab-today">Сегодня ({groups.today.length})</TabsTrigger>
          <TabsTrigger value="overdue" data-testid="tab-overdue">Просрочено ({groups.overdue.length})</TabsTrigger>
          <TabsTrigger value="upcoming" data-testid="tab-upcoming">Предстоящие ({groups.upcoming.length})</TabsTrigger>
          <TabsTrigger value="done" data-testid="tab-done">Done ({groups.done.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="today" className="mt-4">{renderList(groups.today)}</TabsContent>
        <TabsContent value="overdue" className="mt-4">{renderList(groups.overdue)}</TabsContent>
        <TabsContent value="upcoming" className="mt-4">{renderList(groups.upcoming)}</TabsContent>
        <TabsContent value="done" className="mt-4">{renderList(groups.done, false)}</TabsContent>
      </Tabs>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-[#131924] border-[#212b3d] text-slate-100 max-w-lg">
          <DialogHeader><DialogTitle className="font-display">Новое напоминание</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Название *</Label>
              <Input data-testid="reminder-title-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" />
            </div>
            <div>
              <Label>Описание</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Приоритет</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                  <SelectTrigger className="bg-[#0f1521] border-[#212b3d]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#131924] border-[#212b3d] text-slate-100">
                    {Object.entries(REMINDER_PRIORITY).map(([value, meta]) => (
                      <SelectItem key={value} value={value}>{meta.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Срок</Label>
                <Input type="datetime-local" data-testid="reminder-due-input" value={form.due_at} onChange={(e) => setForm({ ...form, due_at: e.target.value })} className="bg-[#0f1521] border-[#212b3d]" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button data-testid="save-reminder-button" onClick={save} className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold">Создать</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
